import React, { Dispatch, SetStateAction, SyntheticEvent, useContext, useState } from 'react';
import { UseFormResult, useServiceRef } from 'hooks';
import { MergedItemsModal, SignInModal, AddressForm } from '@components';
import { colors, Input, Label, Typography } from 'gd-design-library';
import { Customer } from '@commercetools/platform-sdk';
import { AuthorizationService } from '@services';
import { CartContext, UserContext } from '@utils';
import { CustomerInfoFormFieldNames, mapCustomerToFormState } from './form';
import Cookies from 'js-cookie';
import { CartService } from '@services/cart';
import styles from './CustomerInfoForm.module.scss';
import classNames from 'classnames/bind';
import { TENANT_STORAGE_KEY } from '@constants/tenant';

const CN = 'customer-info-form';
const contactInformationSectionClassName = `${CN}--contact-information-section`;
const loginRowClassName = `${contactInformationSectionClassName}--login-row`;
const fieldClassName = `${CN}--field`;
const titleClassName = `${CN}--title`;
const linkButtonClassName = `${CN}--link-button`;
const userInfoClassName = `${CN}--user-info`;
const saveAddressLabelClassName = `${CN}__save-address-label`;

const cx = classNames.bind(styles);

interface Props {
  formData: UseFormResult<CustomerInfoFormFieldNames>;
  saveAddressCheckbox: boolean;
  setSaveAddressCheckbox: Dispatch<SetStateAction<boolean>>
}

export const CustomerInfoForm: React.FC<Props> = ({
  formData: {
    state,
    errors,
    labels,
    handleChange,
    handleBlur,
    handleSelect,
    handleAutofill,
    setFieldValues,
    handleFocus,
    isValid,
    getFormValidState
  },
  saveAddressCheckbox,
  setSaveAddressCheckbox
}) => {
  const [ openLogIn, setOpenLogIn ] = useState(false);
  const [ isAuthLoading, setIsAuthLoading ] = useState(false);
  const [ openMergedItemsModal, setOpenMergedItemsModal ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState('');
  const authServiceRef = useServiceRef(AuthorizationService);
  const cartServiceRef = useServiceRef(CartService);
  const userContext = useContext(UserContext);
  const cartContext = useContext(CartContext);

  const handleOpenLogIn = (): void => {
    setErrorMessage('');
    setOpenLogIn(true);
  };
  const handleCloseLogIn = (): void => {
    setErrorMessage('');
    setOpenLogIn(false);
  };
  const handleOpenMergedItemsModal = (): void => {
    setOpenMergedItemsModal(true);
  };
  const handleCloseMergedItemsModal = (): void => {
    setOpenMergedItemsModal(false);
  };

  const handleSaveShippingAddressCheckbox = (event: SyntheticEvent<Element, Event>): void => {
    const target = event.target as HTMLInputElement;
    setSaveAddressCheckbox(target.checked);
  };

  const handleSignIn = async (email: string, password: string): Promise<void> => {
    const numberOfItemsBeforeMerge = cartContext.cart?.lineItems.length;
    setIsAuthLoading(true);
    const signInResult = await authServiceRef.current.authorizeByPassword(email, password);
    setIsAuthLoading(false);
    if (signInResult?.errorMessage) {
      setErrorMessage(signInResult.errorMessage);

      return;
    }
    setErrorMessage('');
    userContext.setUser(signInResult?.customer ?? null);
    const currentTenantKey = JSON.parse(Cookies.get(TENANT_STORAGE_KEY) || '{}')?.key;
    const updatedCart = await cartServiceRef.current.getMyActiveCart(currentTenantKey);

    cartContext.setCart(updatedCart);
    handleCloseLogIn();

    if (signInResult?.customer) {
      const formState = mapCustomerToFormState(signInResult.customer);
      setFieldValues(formState ?? { email: signInResult.customer.email });
    }

    if (numberOfItemsBeforeMerge !== updatedCart?.lineItems.length) {
      handleOpenMergedItemsModal();
    }
  };

  const handleSignUp = async (customer: Customer): Promise<void> => {
    const numberOfItemsBeforeMerge = cartContext.cart?.lineItems.length;

    setIsAuthLoading(true);
    const signUpResult = await authServiceRef.current.signUp(customer);
    setIsAuthLoading(false);

    if (signUpResult?.errorMessage) {
      setErrorMessage(signUpResult.errorMessage);

      return;
    }

    setErrorMessage('');
    userContext.setUser(signUpResult?.customer ?? null);
    cartContext.setCart(signUpResult?.cart ?? null);
    handleCloseLogIn();

    if (numberOfItemsBeforeMerge !== signUpResult?.cart?.lineItems.length) {
      handleOpenMergedItemsModal();
    }
  };

  const handleLogOut = (): void => {
    if (userContext.user) {
      authServiceRef.current.signOut();
      userContext.setUser(null);
      cartContext.setCart(null);
    }
  };

  return (
    <>
      <form className={ cx(CN) }>
        <div className={ cx(contactInformationSectionClassName) }>
          <div className={ cx(loginRowClassName) }>
            <div>
              <Typography variant='h3'>
                Contact Information
              </Typography>
              {!userContext.user && <Typography variant='small' color={ colors.neutral['grey.80'] }>
                All fields with * are required
              </Typography>}
            </div>
            {!userContext.user ? (
              <Typography
                variant='h6'
                onClick={ handleOpenLogIn }
                className={ cx(linkButtonClassName) }
                role='button'
              >
                Log In
              </Typography>
            ) : (
              <Typography
                onClick={ handleLogOut }
                className={ cx(linkButtonClassName) }
                role='button'
              >
                Log Out
              </Typography>
            )
            }
          </div>
          { userContext.user ?
            <span className={ cx(userInfoClassName) }>
              { `${userContext.user.firstName} ${userContext.user.lastName} (${userContext.user.email})` }
            </span> :
            <Input
              required
              name='email'
              label='Email'
              value={ state.email }
              className={ cx(fieldClassName) }
              onBlur={ handleBlur }
              onChange={ handleChange }
              onFocus={ handleFocus }
              color={ errors.email.isTouched && !errors.email.isValid ? 'error' : undefined }
              helperText={ errors.email.isTouched && !errors.email.isValid ? errors.email.message : '' }
            />
          }
        </div>
        <div className={ cx(titleClassName) }>
          <Typography variant='h3'>
            Shipping Address
          </Typography>
          <Typography variant='small' color={ colors.neutral['grey.80'] }>
            All fields with * are required
          </Typography>
        </div>
        <AddressForm formData={ {
          state,
          errors,
          labels,
          handleChange,
          handleSelect,
          handleBlur,
          handleAutofill,
          handleFocus,
          setFieldValues,
          isValid,
          getFormValidState
        } }/>
        <Label className={ cx(saveAddressLabelClassName) }>
          <Input
            role='checkbox'
            variant='checkbox'
            checked={ saveAddressCheckbox }
            onChange={ handleSaveShippingAddressCheckbox }
          />
          Save this info for the next time
        </Label>
      </form>
      <SignInModal
        open={ openLogIn }
        handleClose={ handleCloseLogIn }
        handleSignIn={ handleSignIn }
        handleSignUp={ handleSignUp }
        isLoading={ isAuthLoading }
        errorMessage={ errorMessage }
      />
      <MergedItemsModal
        open={ openMergedItemsModal }
        handleClose={ handleCloseMergedItemsModal }
      />
    </>
  );
};
